/**
 * Trilho de navegação — fixo, 232px, à esquerda (ver docs/DESIGN.md "Layout").
 * Marca no topo, lista simples de links (item ativo = barra de 2px + peso 600,
 * nunca pílula preenchida, nunca ícone), grupo administrativo separado por
 * filete, e no rodapé a pessoa operando com um menu (perfil / tema / sair).
 * Abaixo de 900px vira barra superior com alternador (ver `.rail` em base.css).
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, type ThemePreference } from '@/hooks/useTheme';
import { initials } from '@/lib/format';

const THEME_LABEL: Record<ThemePreference, string> = {
  light: 'Claro',
  dark: 'Escuro',
  system: 'Sistema',
};

const THEME_ORDER: ThemePreference[] = ['light', 'dark', 'system'];

function railLinkClass({ isActive }: { isActive: boolean }): string {
  return ['rail__link', isActive ? 'is-active' : ''].filter(Boolean).join(' ');
}

export function Rail() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocMouseDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate('/entrar', { replace: true });
  }

  function closeMobile() {
    setMobileOpen(false);
  }

  if (!user) return null;

  return (
    <div className={['rail', mobileOpen ? 'is-open' : ''].filter(Boolean).join(' ')}>
      <div className="rail__brand">
        <p className="rail__brand-title">Central de Propostas</p>
        <p className="rail__brand-sub">Arkmeds</p>
      </div>

      <button
        type="button"
        className="btn btn-ghost btn-sm rail__toggle"
        aria-expanded={mobileOpen}
        aria-controls="rail-nav"
        onClick={() => setMobileOpen((v) => !v)}
      >
        {mobileOpen ? 'Fechar' : 'Menu'}
      </button>

      <nav className="rail__nav" id="rail-nav" aria-label="Principal">
        <div className="rail__nav-group">
          <NavLink to="/" end className={railLinkClass} onClick={closeMobile}>
            Painel
          </NavLink>
          <NavLink to="/nova" className={railLinkClass} onClick={closeMobile}>
            Nova proposta
          </NavLink>
          <NavLink to="/propostas" className={railLinkClass} onClick={closeMobile}>
            Propostas
          </NavLink>
          <NavLink to="/catalogo" className={railLinkClass} onClick={closeMobile}>
            Catálogo
          </NavLink>
          <NavLink to="/clientes" className={railLinkClass} onClick={closeMobile}>
            Clientes
          </NavLink>
        </div>

        <div className="rail__nav-group">
          <NavLink to="/estados" className={railLinkClass} onClick={closeMobile}>
            Estados
          </NavLink>
          {user.role === 'admin' ? (
            <>
              <NavLink to="/usuarios" className={railLinkClass} onClick={closeMobile}>
                Usuários
              </NavLink>
              <NavLink to="/ajustes" className={railLinkClass} onClick={closeMobile}>
                Ajustes
              </NavLink>
            </>
          ) : null}
        </div>
      </nav>

      <div className="rail__foot" ref={menuRef}>
        <button
          type="button"
          className="rail__user"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="rail__user-avatar" aria-hidden="true">
            {initials(user.name)}
          </span>
          <span className="rail__user-text">
            <span className="rail__user-name">{user.name}</span>
            <span className="rail__user-email">{user.email}</span>
          </span>
        </button>

        {menuOpen ? (
          <div className="rail__menu" role="menu">
            <NavLink
              to="/perfil"
              className="rail__menu-item"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                closeMobile();
              }}
            >
              Meu perfil
            </NavLink>
            <hr className="divider rail__menu-divider" />
            <div className="rail__menu-block" role="none">
              <span className="rail__menu-label">Tema</span>
              <div className="rail__menu-theme-row">
                {THEME_ORDER.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="menuitemradio"
                    aria-checked={theme === option}
                    className={`btn btn-sm ${theme === option ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setTheme(option)}
                  >
                    {THEME_LABEL[option]}
                  </button>
                ))}
              </div>
            </div>
            <hr className="divider rail__menu-divider" />
            <button type="button" className="rail__menu-item" role="menuitem" onClick={handleLogout}>
              Sair
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
